import { useEffect } from 'react';
import type { FunctionComponent, MouseEvent, RefObject, UIEvent } from 'react';
import styled, { css } from 'styled-components';

import Popover from '../Popover';
import { useConsolidatedRef, useDirection, useOuterEvent, useEscape, useUID } from '../../hooks';
import type { MenuProps, AcceptedMouseEventElement } from '../Menu/Menu.types';
import Menu from '../Menu';
import { defaultThemeProp } from '../../theme';
import Flex from '../Flex';
import { getFocusables } from '../../utils';
import Text from '../Text';

export interface PresetMenuPopoverProps {
  heading?: string;
  buttonRef: RefObject<HTMLButtonElement>;
  menu?: MenuProps;
  isOpen: boolean;
  onClose: () => void;
}

const StyledPresetMenuPopover = styled(Popover)(({
  theme: {
    base: { 'content-width': contentWidth }
  }
}) => {
  return css`
    min-width: ${contentWidth.xs};
  `;
});

StyledPresetMenuPopover.defaultProps = defaultThemeProp;

const PresetMenuPopover: FunctionComponent<PresetMenuPopoverProps> = ({
  heading,
  buttonRef,
  menu,
  isOpen,
  onClose
}: PresetMenuPopoverProps) => {
  const popoverRef = useConsolidatedRef<HTMLDivElement>();
  const { rtl } = useDirection();
  const headingId = useUID();

  useEscape(e => {
    if (isOpen) {
      onClose();
      e.stopPropagation();
    }
  }, buttonRef);
  useOuterEvent(['mousedown', 'focusin'], [popoverRef], () => {
    if (isOpen) {
      onClose();
    }
  });

  useEffect(() => {
    if (isOpen) getFocusables(popoverRef)[0]?.focus();
  }, [isOpen]);

  return (
    <StyledPresetMenuPopover
      placement={rtl ? 'bottom-end' : 'bottom-start'}
      hideOnTargetHidden
      show={!!menu && isOpen}
      target={buttonRef?.current}
      ref={popoverRef}
      aria-labelledby={headingId}
    >
      {menu && (
        <>
          <Flex container={{ pad: [1, 2] }}>
            <Text variant='h3' id={headingId}>
              {heading}
            </Text>
          </Flex>
          <Menu
            {...menu}
            items={menu.items}
            onItemClick={(itemId, e) => {
              if (
                menu?.mode !== 'multi-select' &&
                (e as MouseEvent<AcceptedMouseEventElement> & UIEvent<AcceptedMouseEventElement>)
                  .detail > 0
              ) {
                onClose();
              }
              menu?.onItemClick?.(itemId, e);
            }}
            focusControlEl={buttonRef?.current ?? undefined}
          />
        </>
      )}
    </StyledPresetMenuPopover>
  );
};
export default PresetMenuPopover;
